/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
	Button,
	Checkbox,
	Chip,
	IconButton,
	Tooltip,
} from '@mui/material';
import {
	Activity,
	BriefcaseBusiness,
	Edit3,
	ExternalLink,
	PackageCheck,
	PlayCircle,
	UploadCloud,
} from 'lucide-react';
import { SelectPicker } from 'rsuite';
import { Add } from '@mui/icons-material';
import { formActions } from '@/Actions';
import { useAuth } from '@/Components/Auth/Login/auth';
import { BackdropLoader } from '@/Components/Loaders';
import MetricCard from '@/Components/NewQualityAnalysisZustand/StatisticalAnalysis/MetricsCard';
import NotAccessiblePage from '@/Pages/NotAccessable/NotAccessable';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { cvatServices, notificationServices } from '@/Services';
import userRolesAndPermissions from '@/Services/userRolesAndPermissions.services';
import ShapeModelLibraryModals from './components/ShapeModelLibraryModals';
import {
	ACTIVE_TRAINING_RUN_STATUSES,
	PREPARING_TRAINING_RUN_STATUSES,
	TERMINAL_TRAINING_RUN_STATUSES,
	canPrepareShapeModelTrainingData,
	canStartShapeModelTraining,
	getMappingLabel,
	getMappingStatus,
	isProductMappedToOtherModel,
	mappedChipClass,
	statusChipClass,
} from './shapeModelLibrary.helpers';

const ENTITY = 'SHAPE_MODEL';

const SHAPE_MODEL_COLUMNS = [
	{
		key: 'shapeName',
		title: 'Shape Name',
		sortable: true,
		fixed: true,
		customCol: true,
		align: 'left',
		width: 260,
	},
	{
		key: 'modelStatus',
		title: 'Status',
		sortable: true,
		customCol: true,
		align: 'center',
		width: 150,
	},
	{
		key: 'modelVersion',
		title: 'Version',
		sortable: true,
		customCol: true,
		align: 'center',
		width: 130,
	},
	{
		key: 'cvatProjectId',
		title: 'CVAT Project',
		sortable: true,
		customCol: true,
		align: 'center',
		width: 160,
	},
	{
		key: 'mappedProductCount',
		title: 'Mapped Products',
		sortable: true,
		customCol: true,
		align: 'center',
		width: 180,
	},
	{
		key: 'actions',
		title: 'Actions',
		customCol: true,
		align: 'center',
		width: 310,
	},
];

const SHAPE_MODEL_COL_MAPPING = {
	shapeName: 'shapeName',
	modelStatus: 'modelStatus',
	modelVersion: 'modelVersionDisplay',
	cvatProjectId: 'cvatProjectIdDisplay',
	mappedProductCount: 'mappedProductCount',
};

const JOB_COLUMNS = [
	{
		key: 'jobId',
		title: 'Job ID',
		sortable: true,
		customCol: true,
		align: 'center',
		width: 120,
	},
	{
		key: 'assigneeId',
		title: 'Assignee',
		customCol: true,
		align: 'center',
		width: 320,
		noPadding: true,
	},
	{
		key: 'stage',
		title: 'Stage',
		customCol: true,
		align: 'center',
		width: 240,
		noPadding: true,
	},
	{
		key: 'state',
		title: 'State',
		customCol: true,
		align: 'center',
		width: 240,
		noPadding: true,
	},
	{
		key: 'actions',
		title: 'Actions',
		customCol: true,
		align: 'center',
		width: 120,
	},
];

const JOB_COL_MAPPING = {
	jobId: 'jobId',
	assigneeId: 'assigneeId',
	stage: 'stage',
	state: 'state',
};

const JOB_STAGE_OPTIONS = ['annotation', 'validation', 'acceptance'];
const JOB_STATE_OPTIONS = ['new', 'in progress', 'rejected', 'completed'];
const JOB_STAGE_PICKER_DATA = [
	{
		label: '-',
		value: '',
	},
	...JOB_STAGE_OPTIONS.map((stage) => ({
		label: stage,
		value: stage,
	})),
];
const JOB_STATE_PICKER_DATA = [
	{
		label: '-',
		value: '',
	},
	...JOB_STATE_OPTIONS.map((state) => ({
		label: state,
		value: state,
	})),
];
const JOB_PICKER_CLASS =
	'w-full min-w-full px-2 py-2 [&_.rs-picker-toggle]:!h-9 [&_.rs-picker-toggle]:!w-full [&_.rs-picker-toggle]:!rounded-md [&_.rs-picker-toggle]:!border [&_.rs-picker-toggle]:!border-slate-300 [&_.rs-picker-toggle]:!bg-white [&_.rs-picker-toggle]:!px-2.5 [&_.rs-picker-toggle]:!text-left [&_.rs-picker-toggle]:!text-sm [&_.rs-picker-toggle:hover]:!border-slate-500';
const JOB_PICKER_MENU_CLASS = 'z-[99999] min-w-[14rem]';
const JOB_PICKER_PLACEMENT = 'bottomStart';
const TRAINING_STATUS_POLL_INTERVAL_MS = 5000;
const ASSIGNABLE_PRODUCTS_PAGE_SIZE = 100;
const MAPPED_PRODUCTS_FETCH_LIMIT = 1000;
const PRODUCT_SEARCH_DEBOUNCE_MS = 350;

const PRODUCT_COLUMNS = [
	{
		key: 'selected',
		title: '',
		customCol: true,
		fixed: true,
		align: 'center',
		width: 72,
	},
	{
		key: 'productId',
		title: 'Product ID',
		sortable: true,
		customCol: true,
		align: 'center',
		width: 140,
	},
	{
		key: 'productName',
		title: 'Product Name',
		sortable: true,
		customCol: true,
		align: 'left',
		width: 420,
	},
	{
		key: 'mappingStatus',
		title: 'Mapped To',
		customCol: true,
		align: 'center',
		width: 210,
	},
];

const PRODUCT_COL_MAPPING = {
	productId: 'productId',
	productName: 'productName',
	mappingStatus: 'mappingStatus',
};

const normalizeProductId = (id) => {
	const parsed = Number(id);
	return Number.isNaN(parsed) ? id : parsed;
};

const normalizeListResponse = (response) => {
	if (Array.isArray(response)) return response;
	return response?.items ?? [];
};

const normalizeArrayResponse = (response, listKey) => {
	const data = response?.data ?? response;
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.items)) return data.items;
	if (listKey && Array.isArray(data?.[listKey])) return data[listKey];
	return [];
};

const getShapeModelId = (item) => {
	return item?.id ?? item?.shapeModelId ?? item?.shape_model_id;
};

const getJobId = (job) => job?.jobId ?? job?.job_id ?? job?.id;

const normalizeJobValue = (value) => value ?? '';

const getOriginalJobValue = (job, field) => {
	return normalizeJobValue(job?.[field]);
};

const getTaskId = (item) => item?.cvatTaskId ?? item?.taskId;

// Console2 users come from /v2.1/users/list — the contract's display rule is
// `displayName || username`, and the picker sends back the numeric `id`.
const getConsoleUserLabel = (user) => {
	return user?.displayName || user?.username || `User ${user?.id ?? '-'}`;
};

const getCreateFailureReason = (createData) => {
	const skipped = createData?.skipped?.[0];
	const failed = createData?.failed?.[0];
	if (skipped?.reason === 'active_task_not_completed') {
		return 'An active task for this shape model is not completed yet';
	}
	if (skipped?.reason) {
		return String(skipped.reason).replace(/_/g, ' ');
	}
	return failed?.error || 'No CVAT task was created';
};

const alignmentClass = {
	left: 'justify-start text-left',
	center: 'justify-center text-center',
	right: 'justify-end text-right',
};

const renderAlignedCell = (content, align = 'left') => {
	return (
		<div
			className={`flex min-h-[1.75rem] w-full items-center ${alignmentClass[align] ?? alignmentClass.left
				}`}
		>
			{content}
		</div>
	);
};

const getActiveModalKey = ({
	shapeModal,
	productModal,
	taskModal,
	jobsModal,
	readinessModal,
	uploadModal,
}) => {
	if (shapeModal.open) return 'shape';
	if (productModal.open) return 'product';
	if (taskModal.open) return 'task';
	if (jobsModal.open) return 'jobs';
	if (readinessModal.open) return 'readiness';
	if (uploadModal.open) return 'upload';
	return null;
};

function ShapeModelLibraryPage(props) {
	const { shapeModelData, shapeModelGateway, userPermissions } = props;
	const { userConfig = {} } = useAuth();
	const roleValues = Object.values(userConfig?.userRolesDict ?? {});
	const isInternal =
		userPermissions?.INTERNAL ||
		roleValues.includes('INTERNAL') ||
		roleValues.includes('CXO Office');
	// CVAT_ASSIGN_MIN_LEVEL — assign / unassign / complete need level >= 3.
	const { level } = userConfig?.userDetails || {};
	const canAssign = level > 2;

	const [loading, setLoading] = useState(false);
	const [shapeModal, setShapeModal] = useState({
		open: false,
		mode: 'CREATE',
		item: null,
	});
	const [shapeForm, setShapeForm] = useState({
		shapeName: '',
	});
	const [shapeFormErrors, setShapeFormErrors] = useState({});
	const [productModal, setProductModal] = useState({
		open: false,
		item: null,
	});
	const [productRows, setProductRows] = useState([]);
	const [productsTotalCount, setProductsTotalCount] = useState(0);
	const [productSearch, setProductSearch] = useState('');
	const [debouncedProductSearch, setDebouncedProductSearch] = useState('');
	const [productPage, setProductPage] = useState(1);
	const [selectedProductIds, setSelectedProductIds] = useState([]);
	const [productLoading, setProductLoading] = useState(false);
	const skipNextSearchFetchRef = useRef(false);
	const initialMappedIdsKeyRef = useRef('');
	const [uploadModal, setUploadModal] = useState({
		open: false,
		item: null,
	});
	const [uploadFile, setUploadFile] = useState(null);
	const [modelVersion, setModelVersion] = useState('');
	const [uploadLoading, setUploadLoading] = useState(false);
	const [taskModal, setTaskModal] = useState({
		open: false,
		item: null,
	});
	const [taskForm, setTaskForm] = useState({
		minSelectionCount: 10,
		assigneeId: null,
	});
	const [taskLoading, setTaskLoading] = useState(false);
	const [jobsModal, setJobsModal] = useState({
		open: false,
		item: null,
		jobs: [],
	});
	const [jobDrafts, setJobDrafts] = useState({});
	const [jobsLoading, setJobsLoading] = useState(false);
	const [consoleUsers, setConsoleUsers] = useState([]);
	const [assignment, setAssignment] = useState(null);
	const [assignmentLoading, setAssignmentLoading] = useState(false);
	// True when the last assignment read failed. `assignment` is then unknown,
	// not absent — every mutation stays locked until an authoritative fetch
	// succeeds, so we can't unassign or complete a row we can't see.
	const [assignmentUnavailable, setAssignmentUnavailable] = useState(false);
	const [readinessModal, setReadinessModal] = useState({
		open: false,
		item: null,
		data: null,
	});
	const [readinessLoading, setReadinessLoading] = useState(false);
	const [prepareLoading, setPrepareLoading] = useState(false);
	const [trainLoading, setTrainLoading] = useState(false);
	const [trainingPollPhase, setTrainingPollPhase] = useState(null);

	const shapeModels = useMemo(() => {
		return normalizeListResponse(shapeModelData?.LIST).map((item) => {
			const cvatTaskId = item?.cvatTaskId ?? item?.taskId;
			return {
				...item,
				id: getShapeModelId(item),
				cvatTaskId,
				modelVersionDisplay: item?.modelVersion ?? '-',
				cvatProjectIdDisplay: item?.cvatProjectId ?? '-',
				mappedProductCount: item?.mappedProductCount ?? 0,
				pendingSelectionCount: item?.pendingSelectionCount ?? 0,
			};
		});
	}, [shapeModelData?.LIST]);

	const statsSummary = useMemo(() => {
		const mappedProducts = shapeModels.reduce((acc, model) => {
			return acc + (model?.mappedProductCount ?? 0);
		}, 0);
		const cvatLinkedModels = shapeModels.filter((model) => {
			return Boolean(model?.cvatProjectId);
		}).length;
		return [
			{
				label: 'Total Shape Models',
				value: shapeModels.length,
				icon: 'ClipboardList',
			},
			{
				label: 'Mapped Products',
				value: mappedProducts,
				icon: 'CircleCheck',
			},
			{
				label: 'CVAT Linked Models',
				value: cvatLinkedModels,
				icon: 'CircleCheck',
			},
		];
	}, [shapeModels]);

	const selectedProductIdSet = useMemo(() => {
		return new Set(selectedProductIds);
	}, [selectedProductIds]);

	const visibleProductIds = useMemo(() => {
		return productRows
			.filter((product) => !isProductMappedToOtherModel(product))
			.map((product) => product?.productId)
			.filter((productId) => productId !== null && productId !== undefined);
	}, [productRows]);

	const totalProductPages = Math.max(
		1,
		Math.ceil(productsTotalCount / ASSIGNABLE_PRODUCTS_PAGE_SIZE)
	);

	const allVisibleProductsSelected =
		visibleProductIds.length > 0 &&
		visibleProductIds.every((productId) => {
			return selectedProductIdSet.has(productId);
		});

	const someVisibleProductsSelected =
		visibleProductIds.some((productId) => {
			return selectedProductIdSet.has(productId);
		}) && !allVisibleProductsSelected;

	const consoleUserPickerData = useMemo(() => {
		return consoleUsers
			// Any level can annotate — level-2 annotators are the expected
			// assignees — but INTERNAL is required to reach the annotation
			// tooling at all, so a non-internal user could never do the work.
			// Filtering on it keeps the list to plausible assignees.
			// `isActive` is checked with !== false so a missing field doesn't
			// silently drop a valid user; `internal` must be positively true.
			.filter((user) => {
				return (
					user?.isActive !== false && Boolean(user?.internal) && user?.id != null
				);
			})
			.map((user) => {
				return {
					label: getConsoleUserLabel(user),
					value: user.id,
				};
			})
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [consoleUsers]);

	const pendingJobUpdateCount = Object.keys(jobDrafts).length;

	const getJobInputValue = (job, field) => {
		const jobId = getJobId(job);
		const draft = jobDrafts[String(jobId)] ?? {};
		if (Object.prototype.hasOwnProperty.call(draft, field)) {
			return draft[field];
		}
		return job?.[field] ?? '';
	};

	const stageJobUpdate = (job, data) => {
		const jobId = getJobId(job);
		if (!jobId) return;
		setJobDrafts((prev) => {
			const key = String(jobId);
			const nextDraft = {
				...(prev[key] ?? {}),
			};
			Object.entries(data).forEach(([field, value]) => {
				const normalizedValue = normalizeJobValue(value);
				const originalValue = getOriginalJobValue(job, field);
				if (normalizedValue === originalValue) {
					delete nextDraft[field];
				} else {
					nextDraft[field] = normalizedValue;
				}
			});
			const next = {
				...prev,
			};
			if (Object.keys(nextDraft).length === 0) {
				delete next[key];
			} else {
				next[key] = nextDraft;
			}
			return next;
		});
	};

	const fetchShapeModels = useCallback(async () => {
		setLoading(true);
		try {
			await shapeModelGateway({
				entity: ENTITY,
				method: 'LIST',
				promisify: true,
				showNotification: false,
				req: {
					params: {
						limit: 100,
						offset: 0,
					},
				},
			});
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
		} finally {
			setLoading(false);
		}
	}, [shapeModelGateway]);

	useEffect(() => {
		if (isInternal) {
			fetchShapeModels();
		}
	}, [isInternal, fetchShapeModels]);

	useEffect(() => {
		const handle = setTimeout(() => {
			setDebouncedProductSearch(productSearch);
		}, PRODUCT_SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(handle);
	}, [productSearch]);

	useEffect(() => {
		if (!productModal.open || !productModal.item) return;
		if (skipNextSearchFetchRef.current) {
			skipNextSearchFetchRef.current = false;
			return;
		}
		let cancelled = false;
		setProductPage(1);
		setProductLoading(true);
		(async () => {
			try {
				await fetchAssignableProducts({
					shapeModel: productModal.item,
					page: 1,
					search: debouncedProductSearch,
				});
			} catch (error) {
				if (!cancelled) {
					notificationServices.generateNotification({
						type: 'error',
						message: error?.message || error,
					});
				}
			} finally {
				if (!cancelled) setProductLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedProductSearch, productModal.open, productModal.item]);

	const syncTrainingPollPhase = useCallback((statusData, preferredPhase = null) => {
		const trainingRunStatus = statusData?.trainingRunStatus ?? null;
		if (preferredPhase === 'prepare') {
			if (ACTIVE_TRAINING_RUN_STATUSES.includes(trainingRunStatus)) {
				setTrainingPollPhase('train');
				return 'train';
			}
			if (
				trainingRunStatus === 'DATA_PREPPED' ||
				TERMINAL_TRAINING_RUN_STATUSES.includes(trainingRunStatus)
			) {
				setTrainingPollPhase(null);
				return null;
			}
			setTrainingPollPhase('prepare');
			return 'prepare';
		}
		if (preferredPhase === 'train') {
			if (TERMINAL_TRAINING_RUN_STATUSES.includes(trainingRunStatus)) {
				setTrainingPollPhase(null);
				return null;
			}
			setTrainingPollPhase('train');
			return 'train';
		}
		if (PREPARING_TRAINING_RUN_STATUSES.includes(trainingRunStatus)) {
			setTrainingPollPhase('prepare');
			return 'prepare';
		}
		if (ACTIVE_TRAINING_RUN_STATUSES.includes(trainingRunStatus)) {
			setTrainingPollPhase('train');
			return 'train';
		}
		setTrainingPollPhase(null);
		return null;
	}, []);

	const openCreateModal = () => {
		setShapeForm({
			shapeName: '',
		});
		setShapeFormErrors({});
		setShapeModal({
			open: true,
			mode: 'CREATE',
			item: null,
		});
	};

	const openEditModal = (item) => {
		setShapeForm({
			shapeName: item?.shapeName ?? '',
		});
		setShapeFormErrors({});
		setShapeModal({
			open: true,
			mode: 'UPDATE',
			item,
		});
	};

	const closeShapeModal = () => {
		setShapeModal({
			open: false,
			mode: 'CREATE',
			item: null,
		});
		setShapeForm({
			shapeName: '',
		});
		setShapeFormErrors({});
	};

	const handleShapeSubmit = async () => {
		const shapeName = shapeForm.shapeName.trim();
		if (!shapeName) {
			setShapeFormErrors({
				shapeName: 'Shape name is required',
			});
			return;
		}

		const isEdit = shapeModal.mode === 'UPDATE';
		try {
			setLoading(true);
			await shapeModelGateway({
				entity: ENTITY,
				method: isEdit ? 'UPDATE' : 'CREATE',
				promisify: true,
				req: {
					data: {
						...(isEdit && {
							shapeModelId:
								shapeModal?.item?.id ??
								shapeModal?.item?.shapeModelId,
						}),
						shapeName,
					},
				},
			});
			closeShapeModal();
			await fetchShapeModels();
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
		} finally {
			setLoading(false);
		}
	};

	const normalizeProductBatch = (batch, pageOffset) => {
		return batch.map((product, index) => {
			const productId = normalizeProductId(product?.productId);
			return {
				...product,
				id: productId,
				productId,
				_fallbackKey: `${pageOffset}-${index}`,
			};
		});
	};

	const fetchMappedProductIds = async (shapeModel) => {
		const shapeModelId = getShapeModelId(shapeModel);
		const response = await shapeModelGateway({
			entity: ENTITY,
			method: 'PRODUCTS',
			promisify: true,
			showNotification: false,
			req: {
				params: {
					shapeModelId,
					mode: 'mapped',
					limit: MAPPED_PRODUCTS_FETCH_LIMIT,
					offset: 0,
				},
			},
		});
		const items = normalizeListResponse(response?.data);
		return items
			.map((product) => normalizeProductId(product?.productId))
			.filter((id) => id !== null && id !== undefined);
	};

	const fetchAssignableProducts = async ({
		shapeModel,
		page = 1,
		search = '',
	}) => {
		const shapeModelId = getShapeModelId(shapeModel);
		const trimmedSearch = search.trim();
		const offset = (page - 1) * ASSIGNABLE_PRODUCTS_PAGE_SIZE;
		const response = await shapeModelGateway({
			entity: ENTITY,
			method: 'PRODUCTS',
			promisify: true,
			showNotification: false,
			req: {
				params: {
					shapeModelId,
					mode: 'assignable',
					limit: ASSIGNABLE_PRODUCTS_PAGE_SIZE,
					offset,
					...(trimmedSearch && { search: trimmedSearch }),
				},
			},
		});
		const data = response?.data ?? {};
		const batch = normalizeProductBatch(
			normalizeListResponse(data),
			offset
		);
		const totalCount = data?.totalCount ?? batch.length + offset;
		setProductsTotalCount(totalCount);
		setProductRows(batch);
	};

	const openProductModal = async (item) => {
		skipNextSearchFetchRef.current = true;
		setProductModal({
			open: true,
			item,
		});
		setProductRows([]);
		setProductsTotalCount(0);
		setSelectedProductIds([]);
		setProductSearch('');
		setDebouncedProductSearch('');
		setProductPage(1);
		setProductLoading(true);
		try {
			const [mappedIds] = await Promise.all([
				fetchMappedProductIds(item),
				fetchAssignableProducts({
					shapeModel: item,
					page: 1,
					search: '',
				}),
			]);
			setSelectedProductIds(mappedIds);
			initialMappedIdsKeyRef.current = [...mappedIds].sort().join(',');
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
		} finally {
			setProductLoading(false);
		}
	};

	const closeProductModal = () => {
		setProductModal({
			open: false,
			item: null,
		});
		setProductRows([]);
		setProductsTotalCount(0);
		setSelectedProductIds([]);
		setProductSearch('');
		setDebouncedProductSearch('');
		setProductPage(1);
	};

	const goToProductPage = async (nextPage) => {
		if (!productModal.open || productLoading) return;
		if (nextPage < 1 || nextPage > totalProductPages) return;
		if (nextPage === productPage) return;
		setProductPage(nextPage);
		setProductLoading(true);
		try {
			await fetchAssignableProducts({
				shapeModel: productModal.item,
				page: nextPage,
				search: debouncedProductSearch,
			});
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
		} finally {
			setProductLoading(false);
		}
	};

	const saveProductAssignments = async () => {
		const currentKey = [...selectedProductIds].sort().join(',');
		if (currentKey === initialMappedIdsKeyRef.current) {
			closeProductModal();
			return;
		}
		try {
			setProductLoading(true);
			await shapeModelGateway({
				entity: ENTITY,
				method: 'SAVE_PRODUCTS',
				promisify: true,
				req: {
					data: {
						shapeModelId: getShapeModelId(productModal?.item),
						productIds: selectedProductIds,
					},
				},
			});
			closeProductModal();
			await fetchShapeModels();
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
		} finally {
			setProductLoading(false);
		}
	};

	const openUploadModal = (item) => {
		setUploadModal({
			open: true,
			item,
		});
		setUploadFile(null);
		setModelVersion('');
	};

	const closeUploadModal = () => {
		setUploadModal({
			open: false,
			item: null,
		});
		setUploadFile(null);
		setModelVersion('');
	};

	const handleUploadSubmit = async () => {
		if (!uploadFile) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Please select a model zip file',
			});
			return;
		}
		if (!uploadFile.name?.toLowerCase?.().endsWith('.zip')) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Only .zip files are allowed',
			});
			return;
		}
		const parsedVersion =
			modelVersion === '' ? '' : parseInt(modelVersion, 10);
		if (
			modelVersion !== '' &&
			(Number.isNaN(parsedVersion) || parsedVersion < 1)
		) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Model version should be a positive integer',
			});
			return;
		}

		try {
			setUploadLoading(true);
			await shapeModelGateway({
				entity: ENTITY,
				method: 'UPLOAD_MODEL',
				promisify: true,
				req: {
					data: {
						shapeModelId: getShapeModelId(uploadModal?.item),
						modelVersion: parsedVersion,
						file: uploadFile,
					},
				},
			});
			closeUploadModal();
			await fetchShapeModels();
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
		} finally {
			setUploadLoading(false);
		}
	};

	const fetchConsoleUsers = useCallback(async () => {
		try {
			const response = await userRolesAndPermissions.getUsersList();
			const users = normalizeArrayResponse(response, 'users');
			setConsoleUsers(users);
			return users;
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
			return [];
		}
	}, []);

	const fetchTaskAssignment = useCallback(async (taskId) => {
		if (!taskId) return null;
		try {
			const response = await cvatServices.fetchTaskAssignments({
				params: {
					taskIds: taskId,
				},
			});
			// A task with no live assignment simply isn't in the list.
			const assignments = response?.data?.assignments ?? [];
			const current =
				assignments.find((entry) => {
					return String(entry?.cvatTaskId) === String(taskId);
				}) ?? null;
			setAssignment(current);
			setAssignmentUnavailable(false);
			return current;
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
			// Drop the stale value rather than keep showing it: someone else may
			// have completed or reassigned the task since we last read it, and
			// acting on the old value could soft-delete completed, payable work.
			setAssignment(null);
			setAssignmentUnavailable(true);
			return null;
		}
	}, []);

	const openTaskModal = (item) => {
		setTaskForm({
			minSelectionCount: 10,
			assigneeId: null,
		});
		setTaskModal({
			open: true,
			item,
		});
		if (canAssign && consoleUsers.length === 0) {
			fetchConsoleUsers();
		}
	};

	const closeTaskModal = () => {
		setTaskModal({
			open: false,
			item: null,
		});
		setTaskForm({
			minSelectionCount: 10,
			assigneeId: null,
		});
	};

	const handleCreateTask = async () => {
		const minSelectionCount = parseInt(taskForm.minSelectionCount, 10);
		if (Number.isNaN(minSelectionCount) || minSelectionCount < 1) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Minimum selection count should be a positive integer',
			});
			return;
		}
		try {
			setTaskLoading(true);
			const response = await cvatServices.createTaskFromQueue({
				data: {
					shapeModelId: getShapeModelId(taskModal?.item),
					minSelectionCount,
				},
			});
			const createData = response?.data ?? response;
			const createdTaskId = createData?.created?.[0]?.taskId;
			// A 200 with an empty `created` list is a skip, not a success — the
			// reason lives in skipped[0] / failed[0].
			if (!createdTaskId) {
				notificationServices.generateNotification({
					type: 'error',
					message: getCreateFailureReason(createData),
				});
				return;
			}

			// The creator is auto-assigned by createFromQueue. If a different
			// annotator was picked, reassign right away — assignment is recorded
			// console-side, so this works while the CVAT import is still running.
			// Kept in its own try/catch: the task already exists at this point, so
			// a failed reassign must not send the user back to re-create it.
			const chosenAssigneeId = taskForm.assigneeId;
			let assignFailed = false;
			if (canAssign && chosenAssigneeId) {
				try {
					await cvatServices.assignTask({
						data: {
							cvatTaskId: createdTaskId,
							userId: chosenAssigneeId,
						},
					});
				} catch (assignError) {
					assignFailed = true;
					notificationServices.generateNotification({
						type: 'error',
						message: `Task ${createdTaskId} created, but assigning it failed: ${assignError?.message || assignError}`,
					});
				}
			}
			if (!assignFailed) {
				notificationServices.generateNotification({
					type: 'success',
					message: `CVAT task ${createdTaskId} created`,
				});
			}
			closeTaskModal();
			await fetchShapeModels();
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
		} finally {
			setTaskLoading(false);
		}
	};

	const closeJobsModal = () => {
		setJobsModal({
			open: false,
			item: null,
			jobs: [],
		});
		setJobDrafts({});
		setAssignment(null);
		setAssignmentUnavailable(false);
	};

	const fetchCvatJobs = async (item) => {
		const taskId = getTaskId(item);
		if (!taskId) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'No CVAT task is mapped to this shape model yet',
			});
			return;
		}
		try {
			setJobsLoading(true);
			setJobsModal((prev) => {
				const previousTaskId = getTaskId(prev?.item);
				return {
					open: true,
					item,
					jobs: prev?.open && previousTaskId === taskId ? prev.jobs : [],
				};
			});
			setJobDrafts({});
			const [jobsResponse] = await Promise.all([
				cvatServices.fetchJobs({
					params: {
						taskId,
					},
				}),
				fetchConsoleUsers(),
				fetchTaskAssignment(taskId),
			]);
			setJobsModal({
				open: true,
				item,
				jobs: normalizeArrayResponse(jobsResponse, 'jobs'),
			});
			setJobDrafts({});
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
		} finally {
			setJobsLoading(false);
		}
	};

	const handleAssignTask = async (userId) => {
		const taskId = getTaskId(jobsModal?.item);
		if (!taskId || assignmentUnavailable) return;
		// The assignment row carries completedAt and the payable counts alongside
		// userId, so re-assigning after completion would credit finished work to
		// someone who did not do it. The control is disabled; this is the guard
		// behind it.
		if (assignment?.completedAt) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'This task is already complete — reassigning it would move its annotation counts to another user',
			});
			return;
		}
		// Clearing the picker unassigns; picking a user is an idempotent upsert.
		if (!userId) {
			await handleUnassignTask();
			return;
		}
		try {
			setAssignmentLoading(true);
			const response = await cvatServices.assignTask({
				data: {
					cvatTaskId: taskId,
					userId,
				},
			});
			setAssignment(response?.data ?? null);
			notificationServices.generateNotification({
				type: 'success',
				message: 'Task assigned',
			});
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
			// The POST failed, so the picker is showing a value the server never
			// accepted — pull the real owner back.
			await fetchTaskAssignment(taskId);
		} finally {
			setAssignmentLoading(false);
		}
	};

	const handleUnassignTask = async () => {
		const taskId = getTaskId(jobsModal?.item);
		if (!taskId || assignmentUnavailable) return;
		// Soft-deleting a completed row would discard its payable counts.
		if (assignment?.completedAt) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'This task is already complete and cannot be unassigned',
			});
			return;
		}
		try {
			setAssignmentLoading(true);
			await cvatServices.unassignTask({
				data: {
					cvatTaskId: taskId,
				},
			});
			setAssignment(null);
			notificationServices.generateNotification({
				type: 'success',
				message: 'Task unassigned',
			});
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
			await fetchTaskAssignment(taskId);
		} finally {
			setAssignmentLoading(false);
		}
	};

	const handleCompleteTask = async () => {
		const taskId = getTaskId(jobsModal?.item);
		if (!taskId || assignmentUnavailable || assignment?.completedAt) return;
		try {
			setAssignmentLoading(true);
			const response = await cvatServices.completeTask({
				data: {
					cvatTaskId: taskId,
				},
			});
			const completed = response?.data ?? null;
			setAssignment(completed);
			notificationServices.generateNotification({
				type: 'success',
				message: `Marked complete — ${completed?.imagesAnnotated ?? 0} images annotated`,
			});
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
			// The server may well have recorded the completion and only the
			// response was lost. Completion is not documented as idempotent, so
			// re-posting could rewrite completedAt and the payable counts — read
			// the authoritative state back instead of leaving the button armed.
			// (A lost response is only fully solved by idempotency server-side.)
			await fetchTaskAssignment(taskId);
		} finally {
			setAssignmentLoading(false);
		}
	};

	const saveJobUpdates = async () => {
		const updates = Object.entries(jobDrafts);
		if (updates.length === 0) return;
		try {
			setJobsLoading(true);
			await Promise.all(
				updates.map(([jobId, data]) => {
					return cvatServices.updateJob({
						jobId,
						data,
					});
				})
			);
			notificationServices.generateNotification({
				type: 'success',
				message: `${updates.length} CVAT job update${updates.length > 1 ? 's' : ''} saved`,
			});
			setJobDrafts({});
			await fetchCvatJobs(jobsModal.item);
			await fetchShapeModels();
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
		} finally {
			setJobsLoading(false);
		}
	};

	const loadTrainingStatus = useCallback(async (item, options = {}) => {
		const {
			showLoading = true,
			refreshList = false,
		} = options;
		try {
			if (showLoading) {
				setReadinessLoading(true);
			}
			const response = await cvatServices.fetchTrainStatus({
				params: {
					shapeModelId: getShapeModelId(item),
				},
			});
			const statusData = response?.data ?? response;
			const requestedShapeModelId = getShapeModelId(item);
			setReadinessModal((prev) => {
				const activeShapeModelId = getShapeModelId(prev?.item);
				if (!prev?.open) {
					return prev;
				}
				if (
					activeShapeModelId !== undefined &&
					activeShapeModelId !== null &&
					requestedShapeModelId !== undefined &&
					requestedShapeModelId !== null &&
					activeShapeModelId !== requestedShapeModelId
				) {
					return prev;
				}
				return {
					open: true,
					item,
					data: statusData,
				};
			});
			if (refreshList) {
				await fetchShapeModels();
			}
			return statusData;
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
			return null;
		} finally {
			if (showLoading) {
				setReadinessLoading(false);
			}
		}
	}, [fetchShapeModels]);

	const startPrepareTrainingData = async (
		item,
		{ showSuccessNotification = true } = {}
	) => {
		const shapeModelId = getShapeModelId(item);
		if (!shapeModelId) return null;
		try {
			setPrepareLoading(true);
			await cvatServices.prepareShapeModelTrainingData({
				data: {
					shapeModelId,
				},
			});
			if (showSuccessNotification) {
				notificationServices.generateNotification({
					type: 'success',
					message: 'Training data preparation started',
				});
			}
			setTrainingPollPhase('prepare');
			const statusData = await loadTrainingStatus(item, {
				showLoading: false,
				refreshList: true,
			});
			syncTrainingPollPhase(statusData, 'prepare');
			return statusData;
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
			return null;
		} finally {
			setPrepareLoading(false);
		}
	};

	const openReadinessModal = async (item) => {
		setReadinessModal({
			open: true,
			item,
			data: null,
		});
		setTrainingPollPhase(null);
		const statusData = await loadTrainingStatus(item, {
			showLoading: true,
			refreshList: true,
		});
		syncTrainingPollPhase(statusData);
	};

	const closeReadinessModal = () => {
		setReadinessModal({
			open: false,
			item: null,
			data: null,
		});
		setTrainingPollPhase(null);
	};

	const handlePrepareTrainingData = async () => {
		await startPrepareTrainingData(readinessModal?.item);
	};

	const handleStartTraining = async () => {
		const item = readinessModal?.item;
		const shapeModelId = getShapeModelId(item);
		if (!shapeModelId) return;
		try {
			setTrainLoading(true);
			await cvatServices.startShapeModelTraining({
				data: {
					shapeModelId,
				},
			});
			notificationServices.generateNotification({
				type: 'success',
				message: 'Shape model training started',
			});
			setTrainingPollPhase('train');
			const statusData = await loadTrainingStatus(item, {
				showLoading: false,
				refreshList: true,
			});
			syncTrainingPollPhase(statusData, 'train');
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message || error,
			});
		} finally {
			setTrainLoading(false);
		}
	};

	const refreshTrainingStatus = async () => {
		const item = readinessModal?.item;
		if (!item) return;
		const statusData = await loadTrainingStatus(item, {
			showLoading: true,
			refreshList: true,
		});
		syncTrainingPollPhase(statusData, trainingPollPhase);
	};

	useEffect(() => {
		if (!readinessModal.open || !readinessModal.item || !trainingPollPhase) {
			return undefined;
		}
		const timeoutId = setTimeout(async () => {
			const statusData = await loadTrainingStatus(readinessModal.item, {
				showLoading: false,
				refreshList: false,
			});
			const nextPhase = syncTrainingPollPhase(statusData, trainingPollPhase);
			if (!nextPhase) {
				await fetchShapeModels();
			}
		}, TRAINING_STATUS_POLL_INTERVAL_MS);
		return () => {
			clearTimeout(timeoutId);
		};
	}, [
		fetchShapeModels,
		loadTrainingStatus,
		readinessModal.item,
		readinessModal.open,
		readinessModal.data?.trainingRunStatus,
		syncTrainingPollPhase,
		trainingPollPhase,
	]);


	const toggleProductSelection = (productId) => {
		if (productId === null || productId === undefined) return;
		setSelectedProductIds((prev) => {
			const next = new Set(prev);
			if (next.has(productId)) {
				next.delete(productId);
			} else {
				next.add(productId);
			}
			return Array.from(next);
		});
	};

	const toggleAllVisibleProducts = () => {
		setSelectedProductIds((prev) => {
			const next = new Set(prev);
			if (allVisibleProductsSelected) {
				visibleProductIds.forEach((productId) => {
					next.delete(productId);
				});
			} else {
				visibleProductIds.forEach((productId) => {
					next.add(productId);
				});
			}
			return Array.from(next);
		});
	};

	const renderTableHeader = (column) => {
		if (column?.key === 'selected') {
			return (
				<Checkbox
					size='small'
					checked={allVisibleProductsSelected}
					indeterminate={someVisibleProductsSelected}
					disabled={visibleProductIds.length === 0}
					onClick={(e) => e.stopPropagation()}
					onChange={(e) => {
						e.stopPropagation();
						toggleAllVisibleProducts();
					}}
				/>
			);
		}
		return renderAlignedCell(column?.title, column?.align);
	};

	const shapeModelTableRows = {
		shapeName: (item) => {
			return renderAlignedCell(item?.shapeName ?? '-', 'left');
		},
		modelStatus: (item) => {
			const status = item?.modelStatus;
			return renderAlignedCell(
				status ? (
					<Chip
						label={status}
						className={`!h-8 !px-1 !text-sm !font-semibold ${statusChipClass(status)}`}
					/>
				) : '-',
				'center'
			);
		},
		modelVersion: (item) => {
			return renderAlignedCell(item?.modelVersionDisplay ?? '-', 'center');
		},
		cvatProjectId: (item) => {
			return renderAlignedCell(item?.cvatProjectIdDisplay ?? '-', 'center');
		},
		mappedProductCount: (item) => {
			return renderAlignedCell(item?.mappedProductCount ?? 0, 'center');
		},
		actions: (item) => {
			const hasTask = Boolean(item?.cvatTaskId ?? item?.taskId);
			const canCreateTask =
				Boolean(item?.cvatProjectId) && (item?.pendingSelectionCount ?? 0) > 0;
			return renderAlignedCell(
				<div className='flex items-center justify-center gap-1'>
					<Tooltip title='Edit shape model'>
						<IconButton
							size='small'
							onClick={() => openEditModal(item)}
						>
							<Edit3 size={18} strokeWidth={1.8} />
						</IconButton>
					</Tooltip>
					<Tooltip title='Assign products'>
						<IconButton
							size='small'
							onClick={() => openProductModal(item)}
						>
							<PackageCheck size={18} strokeWidth={1.8} />
						</IconButton>
					</Tooltip>
					<Tooltip title='Upload model zip'>
						<IconButton
							size='small'
							onClick={() => openUploadModal(item)}
						>
							<UploadCloud size={18} strokeWidth={1.8} />
						</IconButton>
					</Tooltip>
					<Tooltip title='Create CVAT annotation task'>
						<span>
							<IconButton
								size='small'
								disabled={!canCreateTask}
								onClick={() => openTaskModal(item)}
							>
								<PlayCircle size={18} strokeWidth={1.8} />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title='View CVAT jobs'>
						<span>
							<IconButton
								size='small'
								disabled={!hasTask || jobsLoading}
								onClick={() => fetchCvatJobs(item)}
							>
								<BriefcaseBusiness size={18} strokeWidth={1.8} />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title='Training status'>
						<span>
							<IconButton
								size='small'
								disabled={!hasTask}
								onClick={() => openReadinessModal(item)}
							>
								<Activity size={18} strokeWidth={1.8} />
							</IconButton>
						</span>
					</Tooltip>
				</div>,
				'center'
			);
		},
	};

	const productTableRows = {
		selected: (product) => {
			const productId = product?.productId;
			const isMappedToOtherModel = isProductMappedToOtherModel(product);
			return renderAlignedCell(
				<Tooltip
					title={
						isMappedToOtherModel
							? 'Mapped to another shape model'
							: ''
					}
				>
					<span>
						<Checkbox
							size='small'
							checked={selectedProductIdSet.has(productId)}
							disabled={isMappedToOtherModel}
							onClick={(e) => e.stopPropagation()}
							onChange={(e) => {
								e.stopPropagation();
								if (!isMappedToOtherModel) {
									toggleProductSelection(productId);
								}
							}}
						/>
					</span>
				</Tooltip>,
				'center'
			);
		},
		productId: (product) => {
			return renderAlignedCell(product?.productId ?? '-', 'center');
		},
		productName: (product) => {
			return renderAlignedCell(product?.productName ?? '-', 'left');
		},
		mappingStatus: (product) => {
			const mappingStatus = getMappingStatus(product);
			return renderAlignedCell(
				<Chip
					label={getMappingLabel(product)}
					className={`!h-8 !px-1 !text-sm !font-semibold ${mappedChipClass(mappingStatus)}`}
				/>,
				'center'
			);
		},
	};

	const jobTableRows = {
		jobId: (job) => {
			return renderAlignedCell(getJobId(job) ?? '-', 'center');
		},
		// Assignment is still addressed by cvatTaskId, so every row of a task
		// shows the same owner. Fine while a task has one job; when the API
		// accepts a cvatJobId this becomes genuinely per-row.
		assigneeId: () => {
			return renderAlignedCell(
				<SelectPicker
					block
					className={JOB_PICKER_CLASS}
					data={consoleUserPickerData}
					value={assignment?.userId ?? null}
					// rsuite falls back to the placeholder whenever `value` has no
					// matching option, so this is what renders when the assignee is
					// missing from the list — the users fetch failed, or the owner
					// has since been deactivated and filtered out.
					placeholder={
						assignmentUnavailable
							? 'Unavailable'
							: assignment?.userName ?? 'Unassigned'
					}
					disabled={
						!canAssign ||
						assignmentLoading ||
						jobsLoading ||
						assignmentUnavailable ||
						// Owner is frozen once the work is complete and payable.
						Boolean(assignment?.completedAt)
					}
					menuMaxHeight={240}
					menuClassName={JOB_PICKER_MENU_CLASS}
					placement={JOB_PICKER_PLACEMENT}
					container={() => document.body}
					onChange={(value) => {
						// Idempotent upsert — the contract allows POSTing on every
						// change, and clearing the picker unassigns.
						handleAssignTask(value);
					}}
				/>,
				'center'
			);
		},
		stage: (job) => {
			return renderAlignedCell(
				<SelectPicker
					block
					cleanable={false}
					searchable={false}
					className={JOB_PICKER_CLASS}
					data={JOB_STAGE_PICKER_DATA}
					value={getJobInputValue(job, 'stage')}
					menuMaxHeight={220}
					menuClassName={JOB_PICKER_MENU_CLASS}
					placement={JOB_PICKER_PLACEMENT}
					container={() => document.body}
					onChange={(value) => {
						stageJobUpdate(job, {
							stage: value,
						});
					}}
				/>,
				'center'
			);
		},
		state: (job) => {
			return renderAlignedCell(
				<SelectPicker
					block
					cleanable={false}
					searchable={false}
					className={JOB_PICKER_CLASS}
					data={JOB_STATE_PICKER_DATA}
					value={getJobInputValue(job, 'state')}
					menuMaxHeight={220}
					menuClassName={JOB_PICKER_MENU_CLASS}
					placement={JOB_PICKER_PLACEMENT}
					container={() => document.body}
					onChange={(value) => {
						stageJobUpdate(job, {
							state: value,
						});
					}}
				/>,
				'center'
			);
		},
		actions: (job) => {
			const jobUrl = job?.jobUrl ?? job?.job_url ?? job?.url;
			return renderAlignedCell(
				<Tooltip title={jobUrl ? 'Open CVAT job' : 'No CVAT job URL'}>
					<span>
						<IconButton
							size='small'
							disabled={!jobUrl}
							onClick={() => window.open(jobUrl, '_blank')}
						>
							<ExternalLink size={18} strokeWidth={1.8} />
						</IconButton>
					</span>
				</Tooltip>,
				'center'
			);
		},
	};

	if (!isInternal) {
		return <NotAccessiblePage />;
	}

	const activeModalKey = getActiveModalKey({
		shapeModal,
		productModal,
		taskModal,
		jobsModal,
		readinessModal,
		uploadModal,
	});
	const isModalOpen = Boolean(activeModalKey);
	const canStartTraining = canStartShapeModelTraining(readinessModal?.data);
	const canPrepareTrainingData = canPrepareShapeModelTrainingData(
		readinessModal?.data
	);

	return (
		<div className='flex h-[calc(100vh-50px)] w-full flex-col items-stretch gap-4 overflow-auto bg-slate-50 p-4'>
			<BackdropLoader
				showProgress
				isLoading={loading && !isModalOpen}
			/>
			<div className='flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 md:flex-row md:items-center md:justify-between'>
				<h1 className='m-0 text-xl font-bold text-slate-900'>
					Shape Model Library
				</h1>
				<div className='flex flex-wrap items-center gap-3'>
					<Button
						className='!rounded-md !bg-uy-color !px-4 !py-2 !font-medium !normal-case !text-black hover:!bg-yellow-500'
						startIcon={<Add />}
						onClick={openCreateModal}
					>
						Create Shape Model
					</Button>
				</div>
			</div>

			<div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
				{statsSummary.map((stat) => {
					return (
						<MetricCard
							key={stat.label}
							data={stat}
							tabType='stats'
						/>
					);
				})}
			</div>

			<div className='min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white'>
				<VariantTable
					colMapping={SHAPE_MODEL_COL_MAPPING}
					columns={SHAPE_MODEL_COLUMNS}
					data={shapeModels}
					customRowData={shapeModelTableRows}
					customCol={renderTableHeader}
					customInfoLine='No shape models found'
				/>
			</div>

			<ShapeModelLibraryModals
				activeModalKey={activeModalKey}
				shapeModal={shapeModal}
				shapeForm={shapeForm}
				shapeFormErrors={shapeFormErrors}
				setShapeForm={setShapeForm}
				setShapeFormErrors={setShapeFormErrors}
				loading={loading}
				closeShapeModal={closeShapeModal}
				handleShapeSubmit={handleShapeSubmit}
				productModal={productModal}
				productLoading={productLoading}
				productSearch={productSearch}
				setProductSearch={setProductSearch}
				selectedProductIds={selectedProductIds}
				productRows={productRows}
				productsTotalCount={productsTotalCount}
				productPage={productPage}
				totalProductPages={totalProductPages}
				goToProductPage={goToProductPage}
				productTableRows={productTableRows}
				productColMapping={PRODUCT_COL_MAPPING}
				productColumns={PRODUCT_COLUMNS}
				renderTableHeader={renderTableHeader}
				closeProductModal={closeProductModal}
				saveProductAssignments={saveProductAssignments}
				taskModal={taskModal}
				taskLoading={taskLoading}
				taskForm={taskForm}
				setTaskForm={setTaskForm}
				closeTaskModal={closeTaskModal}
				handleCreateTask={handleCreateTask}
				canAssign={canAssign}
				consoleUserPickerData={consoleUserPickerData}
				jobsModal={jobsModal}
				jobsLoading={jobsLoading}
				assignment={assignment}
				assignmentLoading={assignmentLoading}
				assignmentUnavailable={assignmentUnavailable}
				handleUnassignTask={handleUnassignTask}
				handleCompleteTask={handleCompleteTask}
				pendingJobUpdateCount={pendingJobUpdateCount}
				fetchCvatJobs={fetchCvatJobs}
				saveJobUpdates={saveJobUpdates}
				jobTableRows={jobTableRows}
				jobColMapping={JOB_COL_MAPPING}
				jobColumns={JOB_COLUMNS}
				closeJobsModal={closeJobsModal}
				readinessModal={readinessModal}
				readinessLoading={readinessLoading}
				prepareLoading={prepareLoading}
				trainLoading={trainLoading}
				canPrepareTrainingData={canPrepareTrainingData}
				canStartTraining={canStartTraining}
				closeReadinessModal={closeReadinessModal}
				handlePrepareTrainingData={handlePrepareTrainingData}
				refreshTrainingStatus={refreshTrainingStatus}
				handleStartTraining={handleStartTraining}
				uploadModal={uploadModal}
				uploadLoading={uploadLoading}
				modelVersion={modelVersion}
				setModelVersion={setModelVersion}
				uploadFile={uploadFile}
				setUploadFile={setUploadFile}
				closeUploadModal={closeUploadModal}
				handleUploadSubmit={handleUploadSubmit}
			/>
		</div>
	);
}

const mapStateToProps = (state) => {
	return {
		shapeModelData: state.form.SHAPE_MODEL.data,
		userPermissions: state.navigation.userPermissions,
	};
};

const mapDispatchToProps = {
	shapeModelGateway: formActions.gateway,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(ShapeModelLibraryPage);
